import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useSiteData } from "../../context/SiteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", tagline: "", image: "" };

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium outline-none transition focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20";

const DestFormModal = ({ dest, onClose, onSaved }) => {
  const [form, setForm] = useState(dest ? { ...dest } : EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.image.trim()) {
      toast.error("Name and image URL are required.");
      return;
    }
    setBusy(true);
    try {
      if (dest) {
        await axios.put(`${API}/destinations/${dest.id}`, form, { withCredentials: true });
        toast.success(`${form.name} updated.`);
      } else {
        await axios.post(`${API}/destinations`, form, { withCredentials: true });
        toast.success(`${form.name} added.`);
      }
      onSaved();
      onClose();
    } catch {
      toast.error("Could not save the destination.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0b132b]/50 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="dest-form-modal-overlay"
    >
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 30, scale: 0.97 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-3xl bg-white p-7 shadow-2xl"
        data-testid="dest-form-modal"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-xl text-[#0b132b]">{dest ? "Edit Destination" : "Add Destination"}</h3>
          <button data-testid="dest-form-close-button" onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3.5" data-testid="dest-form">
          <input data-testid="dest-form-name-input" value={form.name} onChange={set("name")} placeholder="Destination name *" className={inputCls} />
          <input data-testid="dest-form-tagline-input" value={form.tagline} onChange={set("tagline")} placeholder="Tagline (e.g. Home of the Matterhorn)" className={inputCls} />
          <input data-testid="dest-form-image-input" value={form.image} onChange={set("image")} placeholder="Image URL *" className={inputCls} />
          {form.image && (
            <img src={form.image} alt="Destination preview" className="w-full h-36 object-cover rounded-xl border border-slate-100" data-testid="dest-form-image-preview" />
          )}
          <button
            data-testid="dest-form-save-button"
            type="submit"
            disabled={busy}
            className="w-full rounded-full bg-[var(--brand)] text-white text-sm font-bold py-3 shadow-lg shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)] disabled:opacity-60"
          >
            {busy ? "Saving…" : dest ? "Save Changes" : "Add Destination"}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
};

export const DestinationsTab = () => {
  const { destinations, refreshDestinations } = useSiteData();
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);

  const remove = async (dest) => {
    if (!window.confirm(`Delete ${dest.name}?`)) return;
    try {
      await axios.delete(`${API}/destinations/${dest.id}`, { withCredentials: true });
      toast.success(`${dest.name} removed.`);
      refreshDestinations();
    } catch {
      toast.error("Could not delete the destination.");
    }
  };

  return (
    <div data-testid="admin-destinations-tab">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm font-semibold text-slate-500">{destinations.length} destinations live on the site</p>
        <button
          data-testid="admin-add-destination-button"
          onClick={() => {
            setIsNew(true);
            setEditing({});
          }}
          className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-5 py-2.5 shadow-lg shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)]"
        >
          <Plus className="w-4 h-4" /> Add Destination
        </button>
      </div>

      <div className="grid gap-4" data-testid="admin-destinations-list">
        {destinations.map((d) => (
          <div
            key={d.id}
            data-testid={`admin-dest-row-${d.id}`}
            className="flex items-center gap-4 rounded-2xl bg-white border border-slate-100 p-4 shadow-[0_10px_30px_-16px_rgba(11,19,43,0.15)]"
          >
            <img src={d.image} alt={d.name} className="w-20 h-14 rounded-xl object-cover shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-display font-bold text-[#0b132b] truncate">{d.name}</p>
              <p className="text-xs font-semibold text-slate-400 truncate">{d.tagline}</p>
            </div>
            <button
              data-testid={`admin-dest-edit-${d.id}`}
              onClick={() => {
                setIsNew(false);
                setEditing(d);
              }}
              className="w-9 h-9 rounded-full bg-[var(--brand-soft)] flex items-center justify-center text-[var(--brand)] hover:bg-[var(--brand)] hover:text-white transition-colors"
              aria-label={`Edit ${d.name}`}
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              data-testid={`admin-dest-delete-${d.id}`}
              onClick={() => remove(d)}
              className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center text-red-500 hover:bg-red-500 hover:text-white transition-colors"
              aria-label={`Delete ${d.name}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {editing && (
          <DestFormModal
            dest={isNew ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={refreshDestinations}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
